import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { tokens } from "../../theme";

import SearchIcon from "@mui/icons-material/Search";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LockIcon from "@mui/icons-material/Lock";

import PageGuard from "../../components/PageGuard";
import LoadingState from "../../components/LoadingState";
import { useRedfidViews } from "../../utils/redfidView";
import {
  EMPTY_ARRAY,
  getUniversityFullName,
  normalizeText,
} from "../../utils/utils";

function titleCase(s) {
  const t = String(s ?? "").trim();
  if (!t) return "—";
  return t
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

const TablaParticipantes = () => {
  const colors = tokens();
  const { loading, error, data } = useRedfidViews();

  const apiRef = useGridApiRef();


  const [qNombre, setQNombre] = useState("");

  useEffect(() => {
    const root = apiRef.current?.rootElementRef?.current;
    if (!root) return;

    const headers = root.querySelector(".MuiDataGrid-columnHeaders");
    const scroller = root.querySelector(".MuiDataGrid-virtualScroller");
    if (!headers || !scroller) return;

    const onWheel = (e) => {
      scroller.scrollTop += e.deltaY;
    };

    headers.addEventListener("wheel", onWheel, { passive: true });
    return () => headers.removeEventListener("wheel", onWheel);
  }, [apiRef]);

  const participantes = data?.tables?.participantes ?? EMPTY_ARRAY;


  const rows = useMemo(() => {
    return participantes
      .map((p, idx) => {
        const nombreCompleto = `${p?.nombre ?? ""} ${p?.apellido ?? ""}`.trim();
        const uniFull = getUniversityFullName(p?.universidad);

        return {
          id: p?.id ?? idx,
          nombre: nombreCompleto || "—",
          grado: titleCase(p?.grado),
          disciplina: titleCase(p?.disciplina),
          anio_union: p?.anio_union ?? "—",
          pais: p?.pais ?? "—",
          region: p?.region_name ?? "—",
          universidad: uniFull,
          carrera:
            String(p?.titulo_profesional ?? "").trim() === "Otro"
              ? "Otro Título"
              : p?.titulo_profesional ?? "—",
          programas: p?.programa,
          finalizo_curso: !!p?.finalizo_curso,
          participaciones: Number(p?.encuentros ?? p?.encuentros_asistidos ?? 0),
          presentaciones: Number(p?.presentaciones ?? p?.presentaciones_realizadas ?? 0),
          talleres: Number(p?.talleres ?? p?.talleres_asistidos ?? 0),
          reuniones: Number(p?.reuniones ?? p?.reuniones_asistidas ?? 0),
          adjudica_fondo: !!p?.adjudico_fondo,
          nivel: normalizeText(p?.nivel) || "—",
        };
      })
      .sort((a, b) =>
        String(a.nombre).localeCompare(String(b.nombre), "es", {
          sensitivity: "base",
        })
      );
  }, [participantes]);

  const filteredRows = useMemo(() => {
    const q = normalizeText(qNombre);
    if (!q) return rows;
    return rows.filter((r) => normalizeText(r?.nombre).includes(q));
  }, [rows, qNombre]);


  if (loading) return <LoadingState message="" />;
  if (error) return <div>Error cargando datos: {String(error)}</div>;

  const renderCheck = ({ value }) => (
    <Typography
      fontWeight={900}
      color={value ? colors.green[200] : colors.primary[100]}
      sx={{ width: "100%", textAlign: "center" }}
    >
      {value ? "✔" : "—"}
    </Typography>
  );

  const columns = [
    {
      field: "nombre",
      headerName: "Nombre",
      flex: 1.25,
      minWidth: 260
    },
    {
      field: "grado",
      headerName: "Grado",
      flex: 0.85,
      minWidth: 160
    },
    {
      field: "disciplina",
      headerName: "Disciplina",
      flex: 0.85,
      minWidth: 160
    },
    {
      field: "anio_union",
      headerName: "Año unión",
      flex: 0.6,
      minWidth: 120
    },
    {
      field: "pais",
      headerName: "País",
      flex: 0.6,
      minWidth: 120
    },
    {
      field: "region",
      headerName: "Región",
      flex: 0.6,
      minWidth: 120,
    },
    {
      field: "universidad",
      headerName: "Universidad",
      flex: 1.2,
      minWidth: 280
    },
    {
      field: "carrera",
      headerName: "Título",
      flex: 1.0,
      minWidth: 240
    },

    {
      field: "programas",
      headerName: "Programa",
      flex: 1.1,
      minWidth: 240,
      sortable: true,
      headerAlign: "left",
      align: "left",
      renderCell: ({ value }) => (
        <Typography
          fontSize="14px"
          fontWeight={400}
          color={colors.primary[100]}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
            textAlign: "left",
          }}
          title={value || ""}
        >
          {value || "—"}
        </Typography>
      ),
    },

    {
      field: "finalizo_curso",
      headerName: "Curso",
      flex: 0.55,
      minWidth: 80,
      renderCell: renderCheck
    },
    {
      field: "participaciones",
      headerName: "Encuentros",
      type: "number",
      flex: 0.6,
      minWidth: 110,
      headerAlign: "left"
    },
    {
      field: "presentaciones",
      headerName: "Presentaciones",
      type: "number",
      flex: 0.6,
      minWidth: 140,
      headerAlign: "left"
    },
    {
      field: "talleres",
      headerName: "Talleres",
      type: "number",
      flex: 0.55,
      minWidth: 80,
      headerAlign: "left"
    },
    {
      field: "reuniones",
      headerName: "Reuniones",
      type: "number",
      flex: 0.5,
      minWidth: 100,
      headerAlign: "left"
    },
    {
      field: "adjudica_fondo",
      headerName: "Fondo",
      flex: 0.5,
      minWidth: 90,
      renderCell: renderCheck
    },

    {
      field: "nivel",
      headerName: "Nivel",
      flex: 0.9,
      minWidth: 220,
      sortable: true,
      renderCell: ({ row }) => {
        const nivelRaw = String(row.nivel ?? "").trim().toLowerCase();
        const sinInfo = !nivelRaw || nivelRaw === "—";
        const nivelLabel = sinInfo ? "Sin información" : nivelRaw;

        const bg = sinInfo
          ? "colors.primary[100]"
          : nivelRaw === "experto"
            ? colors.green[200]
            : nivelRaw === "intermedio"
              ? colors.orange[400]
              : colors.yellow[100];

        const Icon = sinInfo
          ? LockIcon
          : nivelRaw === "experto"
            ? EmojiEventsIcon
            : nivelRaw === "intermedio"
              ? AutoAwesomeIcon
              : PsychologyIcon;

        return (
          <Box
            width="80%"
            m="0 auto"
            p="6px 10px"
            display="flex"
            gap="6px"
            alignItems="center"
            justifyContent="center"
            borderRadius="10px"
            sx={{
              backgroundColor: bg,
              border: "1px solid rgba(0,0,0,0.10)"
            }}
          >
            <Icon fontSize="small" />
            <Typography
              color={colors.primary[100]}
              fontWeight={900}
              sx={{
                textTransform: "capitalize",
                fontSize: "14px"
              }}>
              {nivelLabel}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <PageGuard
      title="PARTICIPANTES"
      subtitle="Resumen por persona">
      <Box
        m="20px"
        ml="5px"
        pb="100px">
        <Box
          mt="10px"
          mb="14px"
          sx={{ maxWidth: 520 }}>
          <TextField
            fullWidth
            value={qNombre}
            onChange={(e) => setQNombre(e.target.value)}
            placeholder="Buscar por nombre…"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: colors.primary[200],
                borderRadius: "12px",
              },
            }}
          />
          <Typography
            sx={{
              mt: 0.7,
              fontSize: "12.5px",
              fontWeight: 700,
              color: "rgba(0,0,0,0.55)"
            }}>
            Mostrando {filteredRows.length} de {rows.length}
          </Typography>
        </Box>

        <Box
          mt="20px"
          height="80vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.green[200],
              borderBottom: "1px solid rgba(0,0,0,0.10)",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 900,
              fontSize: "14px",
              color: colors.primary[200],
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid rgba(0,0,0,0.06)",
              fontSize: "14px",
            },
            "& .MuiTablePagination-root": {
              fontSize: "14px",
              color: colors.primary[100]
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "14px",
              fontWeight: 800,
            },
            "& .MuiTablePagination-select": { fontSize: "14px" },
            "& .MuiSvgIcon-root": { fontSize: "22px" },
            "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[200] },
            "& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid ${colors.primary[100]}`,
              backgroundColor: colors.primary[200],
            },
            "& .MuiTablePagination-selectIcon": {
              color: colors.primary[100],
            },
          }}
        >
          <DataGrid
            apiRef={apiRef}
            rows={filteredRows}
            columns={columns}
            rowHeight={56}
            columnHeaderHeight={56}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>
    </PageGuard>
  );
};

export default TablaParticipantes;
